import React from 'react';
import { Link as RRLink } from 'react-router';
import { Box, Chip, CircularProgress, Divider, Link, Stack, Tooltip, Typography } from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { type IntakeStatusBaseSteps, DUPLICATE_DETECTION_PIPELINE_LABELS } from '@shared/patient-intake-status';

import { useDuplicateFlags } from '../../../../../../hooks';
import type { DuplicateFlag } from '../../../../../../hooks/useDuplicateFlags.ts';
import { usePatientIntakeStatusContext } from '../../../hooks';

type AccountVerificationViewerProps = {
  step: IntakeStatusBaseSteps['ACCOUNT_VERIFICATION'];
};


const DetailRow: React.FC<{ label: string; value: string | undefined }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <Stack direction="row" gap={1}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}:
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
};

const FlagCard: React.FC<{ flag: DuplicateFlag; currentPatientId: string }> = ({ flag, currentPatientId }) => {
  const otherPatientId = flag.patientIds.find((id) => id !== currentPatientId) ?? flag.patientIds[0];
  const otherPatient = flag.patients[otherPatientId];

  const flagPipelines = flag.matchedPipelines.filter((p) => p.flag);
  const infoPipelines = flag.matchedPipelines.filter((p) => !p.flag);

  if (!otherPatient) return null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Stack gap={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">
            {otherPatient.firstName} {otherPatient.lastName}
          </Typography>
          <Link component={RRLink} to={`/patients/${otherPatientId}`} target="_blank" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">View patient</Typography>
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Stack>

        <DetailRow label="Email" value={otherPatient.email} />
        <DetailRow label="DOB" value={otherPatient.dob} />
        <DetailRow label="Phone" value={otherPatient.phone} />
        <DetailRow label="Medicare" value={otherPatient.medicareNumber ? `${otherPatient.medicareNumber} (IRN: ${otherPatient.medicareIrn || '—'})` : undefined} />
        <DetailRow label="IHI" value={otherPatient.ihiNumber} />

        {(flagPipelines.length > 0 || infoPipelines.length > 0) && (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {flagPipelines.map((p) => (
              <Tooltip key={p.id} title={`Tier ${p.tier}`} arrow>
                <Chip label={DUPLICATE_DETECTION_PIPELINE_LABELS[p.id] ?? p.label} size="small" color="error" variant="filled" />
              </Tooltip>
            ))}
            {infoPipelines.map((p) => (
              <Tooltip key={p.id} title={`Tier ${p.tier}`} arrow>
                <Chip label={DUPLICATE_DETECTION_PIPELINE_LABELS[p.id] ?? p.label} size="small" color="default" variant="outlined" />
              </Tooltip>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export const AccountVerificationViewer: React.FC<AccountVerificationViewerProps> = ({ step }) => {
  const { patientId, isHealthcareProvider } = usePatientIntakeStatusContext();
  const { data: duplicateFlags, isLoading } = useDuplicateFlags(patientId);

  if (!step) {
    return null;
  }

  const { status } = step.currentStatus;

  if (status === 'COMPLETED') {
    const metadata = step.currentStatus.metadata;
    const resolution = metadata?.resolution;

    return (
      <Stack gap={1.5}>
        <Stack direction="row" alignItems="center" gap={1}>
          <ManageAccountsIcon color="success" />
          <Typography variant="body2" fontWeight="bold">
            {resolution === 'FALSE_POSITIVE'
              ? 'Resolved as False Positive'
              : resolution === 'CONFIRMED_DUPLICATE'
                ? 'Resolved as Confirmed Duplicate'
                : 'Account verification has been resolved.'}
          </Typography>
        </Stack>

        {resolution === 'CONFIRMED_DUPLICATE' && metadata?.primaryAccountId && (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              Primary account:
            </Typography>
            <Link
              component={RRLink}
              to={`/patients/${metadata.primaryAccountId}`}
              target="_blank"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Typography variant="body2">{metadata.primaryAccountId}</Typography>
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          </Stack>
        )}

        {metadata?.resolutionNote && (
          <Typography variant="body2" color="text.secondary">
            Note: {metadata.resolutionNote}
          </Typography>
        )}
      </Stack>
    );
  }

  if (!isHealthcareProvider) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <ManageAccountsIcon color="warning" />
        <Typography variant="body2" fontWeight="bold">
          Your account is being reviewed by our team. No action is required.
        </Typography>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" py={2}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <ManageAccountsIcon color="warning" />
        <Typography variant="body2" fontWeight="bold">
          This account has been flagged as a potential duplicate.
        </Typography>
      </Stack>

      {duplicateFlags.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No duplicate candidates found. The flag may have been cleared by a subsequent identity update.
        </Typography>
      ) : (
        <>
          <Divider />
          <Typography variant="subtitle2">
            Duplicate Candidates ({duplicateFlags.length})
          </Typography>
          <Stack gap={1.5}>
            {duplicateFlags.map((flag) => (
              <FlagCard key={flag.id} flag={flag} currentPatientId={patientId} />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
};
